import React, { Fragment, useState } from 'react'
import { FieldError, useFieldArray, UseFormReturn } from 'react-hook-form'
import { CreateProductOrderReceiptDto, CreateProductOrderReceiptNewProductDto, ProductOrderDto, ProductOrderStatus, ProductType, StorageLocationDto } from '@services/dtos'
import { Button, Checkbox, Dialog, DialogActions, DialogContent, FormHelperText, IconButton, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from '@mui/material'
import { WrapperFormContent } from '../../WrapperFormContent'
import InputField from '@components/inputs/InputField'
import ServerAutocomplete from '@components/autocomplete/ServerAutocomplete'
import { storageLocationService } from '@services/storageLocationService'
import { useSelector } from '@state/store'
import { useAppDispatch } from '@hooks/reduxHooks'
import { addSelectedStorageLocation, removeSelectedNewProduct, removeSelectedStorageLocation } from '@state/reducers/productOrderReceiptReducer'
import { Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material'
import { ChipProductOrderStatus } from '@components/ChipProductOrderStatus'
import { DateInputField } from '@components/inputs/DateInputField'
import moment from 'moment'
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner'
import { QrScanner } from '@yudiel/react-qr-scanner'
import { parseGs1 } from '@utils/parseGs1'
import { enqueueSnackbar } from '@state/reducers/alertReducer'
import SupplierForm from '../../SupplierForm'
import SelectField from '@components/inputs/SelectField'
import { getProductOrders } from '@state/reducers/productOrderReducer'
import { ProductOrderReceiptNewProductForm } from '../components/ProductOrderReceiptNewProductForm'
import { DateTimePicker } from '@mui/x-date-pickers-pro'

type Props = {
  productOrder: ProductOrderDto
  useCreateForm: UseFormReturn<CreateProductOrderReceiptDto, object>
}

const ProductOrderReceiptStepProducts: React.FC<Props> = ({ useCreateForm, productOrder }) => {
  const { control, formState: { errors }, setValue, watch } = useCreateForm
  const [isScanDialogOpen, setScanDialogOpen] = useState(false)
  const [supplierDialogOpen, setSupplierDialogOpen] = useState(false)
  const [pausedQrScanner, setPausedQrScanner] = useState(false)
  const [receiptNewProductFormOpen, setReceiptNewProductFormOpen] = useState(false)
  const { selectedStorageLocations, selectedNewProducts } = useSelector(({ productOrderReceipt }) => ({
    selectedStorageLocations: productOrderReceipt.selectedStorageLocations,
    selectedNewProducts: productOrderReceipt.selectedNewProducts,
  }))
  const dispatch = useAppDispatch()
  const { fields: lineItems, append: addLineItem, remove: removeLineItem } = useFieldArray({
    control,
    name: 'lineItems',
  })
  const { fields: newProducts, append: addNewProduct, remove: removeNewProduct } = useFieldArray({
    control,
    name: 'newProducts',
  })

  const fetchStorageLocations = async (search: string): Promise<StorageLocationDto[]> => {
    const response = await storageLocationService.getAll({ search, limit: 20 })
    return response?.datas ?? []
  }

  return (
    <WrapperFormContent legend={'Contenu livraison'} actionIcon={QrCodeScannerIcon} actionLabel={'Scanner produit'}
      onActionClick={() => setScanDialogOpen(true)} className="h-[calc(100vh-240px)] overflow-y-auto mt-6 mx-auto">
      {(errors.lineItems as unknown as FieldError)?.message && (
        <FormHelperText error sx={{ mb: 2 }}>{(errors.lineItems as unknown as FieldError).message}</FormHelperText>
      )}
      <TableContainer component={Paper} className="overflow-x-auto">
        <Table stickyHeader size="small" className="min-w-[1000px]">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell colSpan={6}>Produit</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>

          <TableBody>
            {productOrder.lineItems.map((item) => {
              const productLineIndexes = lineItems
                .map((li, index) => (li.productId === item.product.id ? index : -1))
                .filter((i) => i !== -1)
              const selected = productLineIndexes.length > 0

              return (
                <Fragment key={item.product.id}>
                  <TableRow hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selected}
                        onChange={(e) => {
                          if (e.target.checked) {
                            addLineItem({
                              productId: item.product.id,
                              receivedUnits: 0,
                              lotNumber: '',
                              storageLocationId: '',
                            })
                            return
                          }
                          productLineIndexes.sort((a, b) => b - a).forEach((i) => removeLineItem(i))
                        }}
                      />
                    </TableCell>
                    <TableCell colSpan={8}>
                      <div className="!flex items-center gap-2">
                        {item.remainingQuantity !== undefined && (
                          <ChipProductOrderStatus
                            text={`${item.quantity - item.remainingQuantity}/${item.quantity} reçu${item.quantity - item.remainingQuantity > 1 ? 's' : ''}`}
                            status={item.remainingQuantity <= 0 ? ProductOrderStatus.RECEIVED : ProductOrderStatus.PARTIALLY_RECEIVED}
                          />
                        )}
                        <Typography variant="body2">{item.product.name}</Typography>
                      </div>
                    </TableCell>
                  </TableRow>
                  {productLineIndexes.map((fieldIndex) => (
                    <TableRow key={lineItems[fieldIndex].id} sx={{
                      '&:nth-of-type(even)': { backgroundColor: 'white' },
                    }}>
                      <TableCell align="right">
                        <InputField
                          disabled={!selected}
                          control={control}
                          name={`lineItems.${fieldIndex}.receivedUnits`}
                          label="Quantité"
                          type="number"
                          sx={{ width: 90 }}
                          error={!!errors.lineItems?.[fieldIndex]?.receivedUnits}
                          helperText={errors.lineItems?.[fieldIndex]?.receivedUnits?.message}
                        />
                      </TableCell>
                      <TableCell>
                        <InputField
                          disabled={!selected}
                          control={control}
                          name={`lineItems.${fieldIndex}.lotNumber`}
                          label="N° de lot"
                          sx={{ width: 150 }}
                          error={!!errors.lineItems?.[fieldIndex]?.lotNumber}
                          helperText={errors.lineItems?.[fieldIndex]?.lotNumber?.message}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <ServerAutocomplete<StorageLocationDto>
                          fetchService={fetchStorageLocations}
                          onChange={(value) => {
                            if (value) {
                              dispatch(addSelectedStorageLocation(value))
                            } else {
                              dispatch(removeSelectedStorageLocation(lineItems[fieldIndex]?.storageLocationId))
                            }
                            setValue(`lineItems.${fieldIndex}.storageLocationId`, value?.id ?? '')
                          }}
                          getValue={(value) => value?.id?.toString() || null}
                          getOptionLabel={(value) => {
                            const storageLocation = value as StorageLocationDto
                            return storageLocation ? storageLocation.name : ''
                          }}
                          debounceMs={300}
                          placeholder="Rechercher un stockage"
                          label="Stockage"
                          minSearchLength={0}
                          width={200}
                          disabled={!selected}
                          value={selectedStorageLocations[lineItems[fieldIndex]?.storageLocationId] || null}
                          error={!!errors.lineItems?.[fieldIndex]?.storageLocationId}
                          helperText={errors.lineItems?.[fieldIndex]?.storageLocationId?.message}
                        />
                      </TableCell>
                      <TableCell>
                        <DateInputField
                          disablePast
                          sx={{ width: 187 }}
                          label="Date expiration"
                          name="expiryAt"
                          value={
                            useCreateForm.watch(`lineItems.${fieldIndex}.expiryAt`)
                              ? moment(useCreateForm.watch(`lineItems.${fieldIndex}.expiryAt`))
                              : null
                          }
                          onChange={(date) => {
                            setValue(
                              `lineItems.${fieldIndex}.expiryAt`,
                              date ? date.toDate() : undefined,
                            )
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        {[ProductType.GENERATOR, ProductType.READY_MADE].includes(item.product.type) && (<InputField
                          disabled={!selected}
                          control={control}
                          name={`lineItems.${fieldIndex}.activityAtRefMbq`}
                          label={`${item.product.type === ProductType.GENERATOR ? 'Dose' : 'Mesure'} 
                            ${item.product.type === ProductType.GENERATOR ? 'GBq' : 'MBq'}`}
                          type="number"
                          sx={{ width: 100 }}
                          error={!!errors.lineItems?.[fieldIndex]?.activityAtRefMbq}
                          helperText={errors.lineItems?.[fieldIndex]?.activityAtRefMbq?.message}
                        />)}
                      </TableCell>
                      <TableCell>
                        <DateTimePicker
                          sx={{ width: 215 }}
                          label="Date calibration (UTC)"
                          name={`lineItems.${fieldIndex}.activityRefAt`}
                          value={(() => {
                            const stored = watch(`lineItems.${fieldIndex}.activityRefAt`)
                            if (!stored) {
                              return null
                            }
                            const d = new Date(stored)
                            return moment(new Date(
                              d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(),
                              d.getUTCHours(), d.getUTCMinutes()
                            ))
                          })()}
                          onChange={(date) => {
                            if (!date) {
                              return setValue(`lineItems.${fieldIndex}.activityRefAt`, undefined)
                            }
                            const utcDate = new Date(Date.UTC(
                              date.year(), date.month(), date.date(),
                              date.hour(), date.minute(), 0
                            ))
                            setValue(`lineItems.${fieldIndex}.activityRefAt`, utcDate)
                          }}
                          slotProps={{ textField: { fullWidth: true } }}
                          disableFuture
                        />
                        {errors.lineItems?.[fieldIndex]?.activityRefAt && <FormHelperText error>
                          {errors.lineItems?.[fieldIndex]?.activityRefAt?.message}
                        </FormHelperText>}
                      </TableCell>
                      <TableCell>
                        {item.product.type === ProductType.GENERATOR && (productOrder.supplier.productionSites.length > 0 ? (
                          <Stack direction="row" alignItems="center">
                            <SelectField
                              control={control}
                              name={`lineItems.${fieldIndex}.productionSite`}
                              options={productOrder.supplier.productionSites.map((productionSite) => ({
                                label: productionSite,
                                value: productionSite,
                              }))}
                              value={watch(`lineItems.${fieldIndex}.productionSite`)}
                              label="Lieu production"
                              sx={{ width: 130 }}
                            />
                            <Tooltip title="Ajouter un lieu de production">
                              <IconButton
                                size="small"
                                disabled={!selected}
                                onClick={() => setSupplierDialogOpen(true)}
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        ) : <Button variant="outlined" size="small" onClick={() => setSupplierDialogOpen(true)}>
                          <Typography variant="caption">
                            Ajouter un lieu de production
                          </Typography>
                        </Button>)}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => { removeLineItem(fieldIndex) }}>
                          <DeleteIcon color="primary" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {selected && (
                    <TableRow>
                      <TableCell />
                      <TableCell colSpan={7}>
                        <Button
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() =>
                            addLineItem({
                              productId: item.product.id,
                              receivedUnits: 0,
                              lotNumber: '',
                              storageLocationId: '',
                            })
                          }
                        >
                          Nouvelle ligne (autre stockage, lot...)
                        </Button>
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Button 
        onClick={() => setReceiptNewProductFormOpen(true)} 
        size='small' 
        variant='outlined'
        sx={{ mt: 4 }}>
          Ajouter un produit de remplacement
      </Button>

      {Object.values(selectedNewProducts).length > 0 && (<TableContainer component={Paper} className="overflow-x-auto mt-2">
        <Table stickyHeader size="small" className="min-w-[1000px]">
          <TableHead>
            <TableRow>
              <TableCell colSpan={6}>Produit de remplacements</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>

          <TableBody>
            {Object.values(selectedNewProducts).map((selectedNewProduct) => {
              const item = newProducts.find((np) => np.productId === selectedNewProduct.id)
              if (!item) return null
              const newProductIndexes = newProducts
                .map((li, index) => (li.productId === item.productId ? index : -1))
                .filter((i) => i !== -1)
              const newProduct = selectedNewProducts[item.productId]

              return (
                <Fragment key={item.productId}>
                  <TableRow hover>
                    <TableCell colSpan={7}>
                      <Typography variant="body2">
                        {productOrder.lineItems.find(({ product }) => product.id === item.baseProductId)?.product.name} → {newProduct.name}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  {newProductIndexes.map((fieldIndex) => (
                    <TableRow key={newProducts[fieldIndex].id} sx={{
                      '&:nth-of-type(even)': { backgroundColor: 'white' },
                    }}>
                      <TableCell align="right">
                        <InputField
                          control={control}
                          name={`newProducts.${fieldIndex}.receivedUnits`}
                          label="Quantité"
                          type="number"
                          sx={{ width: 90 }}
                          error={!!errors.newProducts?.[fieldIndex]?.receivedUnits}
                          helperText={errors.newProducts?.[fieldIndex]?.receivedUnits?.message}
                        />
                      </TableCell>
                      <TableCell>
                        <InputField
                          control={control}
                          name={`newProducts.${fieldIndex}.lotNumber`}
                          label="N° de lot"
                          sx={{ width: 150 }}
                          error={!!errors.newProducts?.[fieldIndex]?.lotNumber}
                          helperText={errors.newProducts?.[fieldIndex]?.lotNumber?.message}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <ServerAutocomplete<StorageLocationDto>
                          fetchService={fetchStorageLocations}
                          onChange={(value) => {
                            if (value) {
                              dispatch(addSelectedStorageLocation(value))
                            } else {
                              dispatch(removeSelectedStorageLocation(newProducts[fieldIndex]?.storageLocationId))
                            }
                            setValue(`newProducts.${fieldIndex}.storageLocationId`, value?.id ?? '')
                          }}
                          getValue={(value) => value?.id?.toString() || null}
                          getOptionLabel={(value) => {
                            const storageLocation = value as StorageLocationDto
                            return storageLocation ? storageLocation.name : ''
                          }}
                          debounceMs={300}
                          placeholder="Rechercher un stockage"
                          label="Stockage"
                          minSearchLength={0}
                          width={200}
                          value={selectedStorageLocations[newProducts[fieldIndex]?.storageLocationId] || null}
                          error={!!errors.newProducts?.[fieldIndex]?.storageLocationId}
                          helperText={errors.newProducts?.[fieldIndex]?.storageLocationId?.message}
                        />
                      </TableCell>
                      <TableCell>
                        <DateInputField
                          disablePast
                          sx={{ width: 187 }}
                          label="Date expiration"
                          name="expiryAt"
                          value={
                            useCreateForm.watch(`newProducts.${fieldIndex}.expiryAt`)
                              ? moment(useCreateForm.watch(`newProducts.${fieldIndex}.expiryAt`))
                              : null
                          }
                          onChange={(date) => {
                            setValue(
                              `newProducts.${fieldIndex}.expiryAt`,
                              date ? date.toDate() : undefined,
                            )
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        {[ProductType.GENERATOR, ProductType.READY_MADE].includes(newProduct.type) && (<InputField
                          control={control}
                          name={`newProducts.${fieldIndex}.activityAtRefMbq`}
                          label={`${newProduct.type === ProductType.GENERATOR ? 'Dose' : 'Mesure'} 
                            ${newProduct.type === ProductType.GENERATOR ? 'GBq' : 'MBq'}`}
                          type="number"
                          sx={{ width: 100 }}
                          error={!!errors.newProducts?.[fieldIndex]?.activityAtRefMbq}
                          helperText={errors.newProducts?.[fieldIndex]?.activityAtRefMbq?.message}
                        />)}
                      </TableCell>
                      <TableCell>
                        <DateTimePicker
                          sx={{ width: 215 }}
                          label="Date calibration (UTC)"
                          name={`newProducts.${fieldIndex}.activityRefAt`}
                          value={(() => {
                            const stored = watch(`newProducts.${fieldIndex}.activityRefAt`)
                            if (!stored) {
                              return null
                            }
                            const d = new Date(stored)
                            return moment(new Date(
                              d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(),
                              d.getUTCHours(), d.getUTCMinutes()
                            ))
                          })()}
                          onChange={(date) => {
                            if (!date) {
                              return setValue(`newProducts.${fieldIndex}.activityRefAt`, undefined)
                            }
                            const utcDate = new Date(Date.UTC(
                              date.year(), date.month(), date.date(),
                              date.hour(), date.minute(), 0
                            ))
                            setValue(`newProducts.${fieldIndex}.activityRefAt`, utcDate)
                          }}
                          slotProps={{ textField: { fullWidth: true } }}
                          disableFuture
                        />
                        {errors.newProducts?.[fieldIndex]?.activityRefAt && <FormHelperText error>
                          {errors.newProducts?.[fieldIndex]?.activityRefAt?.message}
                        </FormHelperText>}
                      </TableCell>
                      <TableCell>
                        {newProduct.type === ProductType.GENERATOR && (productOrder.supplier.productionSites.length > 0 ? (
                          <Stack direction="row" alignItems="center">
                            <SelectField
                              control={control}
                              name={`newProducts.${fieldIndex}.productionSite`}
                              options={productOrder.supplier.productionSites.map((productionSite) => ({
                                label: productionSite,
                                value: productionSite,
                              }))}
                              value={watch(`newProducts.${fieldIndex}.productionSite`)}
                              label="Lieu production"
                              sx={{ width: 130 }}
                            />
                            <Tooltip title="Ajouter un lieu de production">
                              <IconButton
                                size="small"
                                onClick={() => setSupplierDialogOpen(true)}
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        ) : <Button variant="outlined" size="small" onClick={() => setSupplierDialogOpen(true)}>
                          <Typography variant="caption">
                            Ajouter un lieu de production
                          </Typography>
                        </Button>)}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => {
                            if (newProducts.filter(({ productId }) => productId === item.productId).length === 1) {
                              dispatch(removeSelectedNewProduct(item.productId))
                            }
                            removeNewProduct(fieldIndex)
                          }}>
                          <DeleteIcon color="primary" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell />
                    <TableCell colSpan={7}>
                      <Button
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() =>
                          addNewProduct({
                            baseProductId: item.baseProductId,
                            productId: item.productId,
                            receivedUnits: 0,
                            lotNumber: '',
                            storageLocationId: '',
                          })
                        }
                      >
                        Nouvelle ligne (autre stockage, lot...)
                      </Button>
                    </TableCell>
                  </TableRow>
                </Fragment>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>)}

      <Dialog 
        open={isScanDialogOpen} 
        maxWidth="sm" 
        fullWidth 
        onClose={() => {
          setScanDialogOpen(false)
          setPausedQrScanner(false)
        }}>
        <DialogActions>
          <Button
            onClick={() => setScanDialogOpen(false)}
            variant="outlined">
            Fermer
          </Button>
        </DialogActions>
        <DialogContent>
          <QrScanner 
            stopDecoding={pausedQrScanner}
            onDecode={(result) => {
              setPausedQrScanner(true)
              const parsed = parseGs1(result)
              if (!parsed) {
                dispatch(
                  enqueueSnackbar({
                    message: 'Format non reconnu',
                    options: { variant: 'error' },
                  }),
                )
                setPausedQrScanner(false)
                return
              }
              const product = productOrder.lineItems.find(({ product }) => product.gtinEan === parsed.gtin)?.product
              if (!product) {
                dispatch(
                  enqueueSnackbar({
                    message: 'Produit non trouvé pour cette commande',
                    options: { variant: 'error' },
                  }),
                )
                setPausedQrScanner(false)
                setScanDialogOpen(false)
                return
              }
              const existingItem = lineItems.find(
                (item) => item.productId === product.id && item.lotNumber === parsed.lotNumber
              )
              if (existingItem) {
                dispatch(
                  enqueueSnackbar({
                    message: 'Produit déjà présent avec ce n° de lot',
                    options: { variant: 'error' },
                  }),
                )
                setPausedQrScanner(false)
                setScanDialogOpen(false)
                return
              }
              addLineItem({
                productId: product.id,
                receivedUnits: 1,
                lotNumber: parsed.lotNumber ?? '',
                expiryAt: parsed.expirationDate ?? undefined
              })
              setPausedQrScanner(false)
              setScanDialogOpen(false)
            }}
          />
        </DialogContent>
      </Dialog>

      <Dialog 
        open={supplierDialogOpen} 
        maxWidth="md" 
        fullWidth 
        onClose={() => setSupplierDialogOpen(false)}>
        <SupplierForm 
          highlightProductionSites
          editingSupplier={productOrder.supplier} 
          onClose={() => {
            dispatch(getProductOrders())
            setSupplierDialogOpen(false)}
          } />
      </Dialog>

      <Dialog 
        open={receiptNewProductFormOpen} 
        maxWidth="md" 
        fullWidth 
        onClose={() => setReceiptNewProductFormOpen(false)}>
        <ProductOrderReceiptNewProductForm 
          onClose={() => setReceiptNewProductFormOpen(false)}
          orderProducts={productOrder.lineItems.map(({ product }) => product)} 
          onSubmit={(data: CreateProductOrderReceiptNewProductDto) => addNewProduct({
            ...data,
            receivedUnits: 0,
            lotNumber: '',
            storageLocationId: '',
          })} />
      </Dialog>
    </WrapperFormContent>
  )
}

export default ProductOrderReceiptStepProducts
